// External packages
import * as ReactAria from 'react-aria-components';
import * as React from 'react';

// Components
import { Icon } from 'components/Icons';

// Utils
import { twJoin, twMerge } from 'tailwind-merge';

const getInputClassNames = ({
  colorScheme,
  isTextArea = false,
}: InputOwnProps & { isTextArea?: boolean }) => {
  const generalClasses =
    'h-14 w-full rounded-lg border border-gray-200 px-4 transition-colors placeholder:text-gray-500 valid:hover:border-gray-400 data-disabled:pointer-events-none data-disabled:bg-gray-50 data-disabled:text-gray-200 data-disabled:placeholder:text-gray-200 data-invalid:border-red-600 data-invalid:text-red-600 data-invalid:outline-red-600 data-invalid:placeholder:text-red-600 data-invalid:hover:border-red-800';

  const colorSchemeClasses = {
    white: 'bg-white',
    gray: 'bg-gray-75',
  }[colorScheme!];

  const additionalClasses = isTextArea ? 'min-h-45 max-h-90 p-4' : 'h-14';

  return twJoin(generalClasses, colorSchemeClasses, additionalClasses);
};

export const TextField = ReactAria.TextField;

export type LabelProps = ReactAria.LabelProps;

export const Label: React.FC<LabelProps> = ({ className, ...props }) => (
  <ReactAria.Label
    {...props}
    className={twMerge(
      'mb-2 block text-gray-600 group-data-required:after:content-["*"] group-data-required:after:ml-1 group-data-required:after:text-red-600',
      className
    )}
  />
);

export type SubLabelProps = {
  colorScheme?: 'success' | 'error';
} & ReactAria.TextProps;

export const SubLabel: React.FC<SubLabelProps> = ({
  colorScheme,
  className,
  ...props
}) => (
  <ReactAria.Text
    {...props}
    className={twMerge(
      'mt-2 block text-xs',
      colorScheme === 'success' && 'text-green-700',
      colorScheme === 'error' && 'text-red-600',
      className
    )}
  />
);

type InputOwnProps = {
  colorScheme?: 'white' | 'gray';
};

export type InputProps = ReactAria.InputProps & InputOwnProps;

export const Input: React.FC<InputProps> = ({
  colorScheme = 'gray',
  className,
  ...props
}) => (
  <ReactAria.Input
    {...props}
    className={twMerge(
      getInputClassNames({ colorScheme, isTextArea: false }),
      className as string
    )}
  />
);

export type TextAreaProps = ReactAria.TextAreaProps & InputOwnProps;

export const TextArea: React.FC<TextAreaProps> = ({
  colorScheme = 'gray',
  className,
  ...props
}) => (
  <ReactAria.TextArea
    {...props}
    className={twMerge(
      getInputClassNames({ colorScheme, isTextArea: true }),
      className as string
    )}
  />
);

export const InputPassword: React.FC<InputProps> = ({ ...props }) => {
  const [passwordVisible, setPasswordVisible] = React.useState(false);

  return (
    <div className="relative">
      <Input {...props} type={passwordVisible ? 'text' : 'password'} />
      <ReactAria.Button
        onPress={() => setPasswordVisible((value) => !value)}
        className="absolute top-1/2 right-4 -translate-y-1/2 transform cursor-pointer text-gray-500"
      >
        <Icon name={passwordVisible ? 'eye' : 'eye-slash'} />
      </ReactAria.Button>
    </div>
  );
};
